import { Card, Typography, Button } from "antd";
import {
  SettingOutlined,
  ApartmentOutlined,
  ShopOutlined,
  PrinterOutlined,
  DollarOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

interface Props {
  onOpenConfig: () => void;
  onOpenFeatures: () => void;
  onOpenStores: () => void;
  onOpenPrinters: () => void;
  onOpenPayments: () => void;
  onOpenPackage: () => void;
}

export const TenantActionsGrid: React.FC<Props> = ({
  onOpenConfig,
  onOpenFeatures,
  onOpenStores,
  onOpenPrinters,
  onOpenPayments,
  onOpenPackage,
}) => {
  const actions = [
    {
      key: "config",
      icon: <SettingOutlined className="text-lg text-blue-500" />,
      bgIcon: "bg-blue-50/50",
      title: "System configuration",
      description: "Tenant name, sub-name, and other config values.",
      buttonLabel: "Manage config",
      onClick: onOpenConfig,
    },
    {
      key: "package",
      icon: <AppstoreOutlined className="text-lg text-fuchsia-500" />,
      bgIcon: "bg-fuchsia-50/50",
      title: "Package assignment",
      description: "Assign a feature package and track its history.",
      buttonLabel: "Manage package",
      onClick: onOpenPackage,
    },
    {
      key: "features",
      icon: <ApartmentOutlined className="text-lg text-purple-500" />,
      bgIcon: "bg-purple-50/50",
      title: "Feature management",
      description: "Enable or disable sub-features for the tenant.",
      buttonLabel: "Manage features",
      onClick: onOpenFeatures,
    },
    {
      key: "stores",
      icon: <ShopOutlined className="text-lg text-amber-500" />,
      bgIcon: "bg-amber-50/50",
      title: "Store management",
      description: "Create, edit, and manage stores for the tenant.",
      buttonLabel: "Manage stores",
      onClick: onOpenStores,
    },
    {
      key: "printers",
      icon: <PrinterOutlined className="text-lg text-cyan-500" />,
      bgIcon: "bg-cyan-50/50",
      title: "Printer management",
      description: "Manage printers and their assignments to stores.",
      buttonLabel: "Manage printers",
      onClick: onOpenPrinters,
    },
    {
      key: "payments",
      icon: <DollarOutlined className="text-lg text-green-500" />,
      bgIcon: "bg-green-50/50",
      title: "Payment management",
      description: "View and manage payments and billing records.",
      buttonLabel: "Manage payments",
      onClick: onOpenPayments,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {actions.map((action) => (
        <Card
          key={action.key}
          className="rounded-2xl hover:shadow-md transition-all duration-300 border-gray-100 flex flex-col justify-between"
          styles={{
            body: {
              padding: 20,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            },
          }}
        >
          <div className="flex-1">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl ${action.bgIcon} border border-gray-100/30 mb-4`}
            >
              {action.icon}
            </div>
            <Text strong className="block mb-1.5 text-gray-800 text-sm">
              {action.title}
            </Text>
            <Text
              type="secondary"
              className="text-[12px] block mb-4 min-h-[40px] leading-normal"
            >
              {action.description}
            </Text>
          </div>
          <Button
            block
            onClick={action.onClick}
            className="rounded-xl h-10 font-medium text-xs border-gray-200 hover:border-gray-900 hover:text-gray-900 transition-all"
          >
            {action.buttonLabel}
          </Button>
        </Card>
      ))}
    </div>
  );
};
